import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { dataIt } from '../../lib/eventi/stati';
import { formatCurrency } from '../../lib/subscriptionUtils';
import type { BigliettoEvento } from '../../lib/eventi/tipi';
import { FatturaOrdine } from './FatturaOrdine';

type Props = {
  ordine: BigliettoEvento['ordine'];
  /** Il centro della carta: i biglietti (scheda Biglietti) o cosa ha comprato (Fatture). */
  children?: ReactNode;
  onCambiata: () => Promise<void>;
};

/** Lo stato dell'ordine su WooCommerce, come pastiglia. */
const STATI_ORDINE: Record<string, { testo: string; classe: string }> = {
  processing: { testo: 'Pagato', classe: 'border-green-200 bg-green-50 text-green-700' },
  completed: { testo: 'Completato', classe: 'border-green-200 bg-green-50 text-green-700' },
  cancelled: { testo: 'Annullato', classe: 'border-red-200 bg-red-50 text-red-700' },
  refunded: { testo: 'Rimborsato', classe: 'border-red-200 bg-red-50 text-red-700' },
  failed: { testo: 'Pagamento fallito', classe: 'border-red-200 bg-red-50 text-red-700' },
};

/**
 * Un ordine del sito come carta a se': chi ha comprato e l'ordine in alto,
 * il contenuto al centro, la fattura in basso su fondo grigio. La usano la
 * scheda Biglietti di un evento e Fatture → Ordini dal sito.
 */
export function CartaOrdine({ ordine, children, onCambiata }: Props) {
  const stato = STATI_ORDINE[ordine.stato] ?? { testo: ordine.stato, classe: 'border-gray-200 bg-gray-50 text-neutral-600' };
  const conNome = ordine.acquirente !== ordine.email;
  return (
    <li className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-col gap-2 px-4 pt-4 sm:flex-row sm:items-start sm:justify-between md:px-5">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-gray-900">{ordine.acquirente}</p>
          <p className="truncate text-xs text-neutral-500">
            {[ordine.azienda, conNome ? ordine.email : null].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 text-xs text-neutral-500 sm:justify-end">
          <span>
            Ordine <span className="font-medium text-gray-800">n. {ordine.numero}</span>
          </span>
          <span aria-hidden>·</span>
          <span>{dataIt(ordine.pagato_il ?? ordine.creato_il)}</span>
          {ordine.totale !== null && (
            <>
              <span aria-hidden>·</span>
              <span className="font-medium text-gray-800">{formatCurrency(ordine.totale)}</span>
            </>
          )}
          <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-semibold', stato.classe)}>{stato.testo}</span>
        </div>
      </header>

      {children && <div className="px-4 py-3 md:px-5">{children}</div>}

      <footer className={cn('border-t border-gray-100 bg-gray-50/80 px-4 py-3 md:px-5', !children && 'mt-3')}>
        <FatturaOrdine ordine={ordine} onCambiata={onCambiata} />
      </footer>
    </li>
  );
}
